import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSalon } from '../context/SalonContext';
import './Navbar.css';
import { FiVolume2, FiVolumeX, FiFacebook, FiInstagram, FiUser, FiLogOut, FiSettings, FiGrid } from 'react-icons/fi';
import { FaTiktok } from 'react-icons/fa'; // For consistency if used
import AuthModal from './AuthModal';
import { isAuthenticated, logout, getCurrentUser } from '../services/pocketbase';

const defaultFb = "https://www.facebook.com/people/Studio-Ambre-Coiffure-Samatan/100093118543312/";
const defaultInsta = "https://www.instagram.com/studio_ambre32?igsh=Nm5ncms0amV0NnBl";

function Navbar({ isMusicPlaying, toggleMusic }) {
  const { salonInfos } = useSalon();
  const facebookUrl = salonInfos?.social_facebook || defaultFb;
  const instagramUrl = salonInfos?.social_instagram || defaultInsta;
  const tiktokUrl = salonInfos?.social_tiktok || ''; // Future use

  const navigate = useNavigate();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Auth state
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [user, setUser] = useState(null);

  // Dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleMobileMenu = () => setMobileMenuOpen(!isMobileMenuOpen);

  useEffect(() => {
    checkAuthStatus();
    const handleStorageChange = () => checkAuthStatus();
    const interval = setInterval(checkAuthStatus, 2000);
    window.addEventListener('storage', handleStorageChange);

    // Click outside handler
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('mousedown', handleClickOutside);
      clearInterval(interval);
    };
  }, []);

  const checkAuthStatus = () => {
    const loggedIn = isAuthenticated();
    setIsUserLoggedIn(loggedIn);
    if (loggedIn) {
      setUser(getCurrentUser());
    } else {
      setUser(null);
      setDropdownOpen(false); // Close dropdown if logged out
    }
  };

  const handleUserIconClick = (e) => {
    e.preventDefault();
    checkAuthStatus();

    if (isAuthenticated()) {
      // Toggle dropdown
      setDropdownOpen(!dropdownOpen);
    } else {
      setAuthModalOpen(true);
    }
  };

  const handleLogout = (e) => {
    e?.preventDefault();
    setDropdownOpen(false);
    logout();
    checkAuthStatus();
    if (window.location.pathname.startsWith('/admin') || window.location.pathname.startsWith('/profil')) {
      navigate('/');
    }
  };

  const navigateTo = (path) => {
    setDropdownOpen(false);
    navigate(path);
  };

  const closeMenuAndScroll = (event, href) => {
    if (!href) return;

    if (href.startsWith('#')) {
      if (window.location.pathname === '/' || window.location.pathname === '') {
        event.preventDefault();
        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        if (window.location.pathname !== '/') {
          event.preventDefault();
          navigate('/');
          setTimeout(() => {
            const targetId = href.substring(1);
            const el = document.getElementById(targetId);
            if (el) el.scrollIntoView();
          }, 100);
        }
      }
    }

    if (isMobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 70);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", text: "Le Salon" },
    { href: "#services", text: "Nos Services" },
    { href: "#tarifs", text: "Tarifs" },
    { href: "#gallery", text: "Galerie" },
    { href: "#location", text: "Horaires & Accès" },
    { href: "#reservation", text: "Réserver" }
  ];

  return (
    <>
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${isMobileMenuOpen ? 'mobile-menu-active' : ''}`}>
        <div className="navbar-container">
          <a href="#hero" className="navbar-logo" onClick={(e) => closeMenuAndScroll(e, "#hero")}>
            Studio Ambre
          </a>

          <ul className="navbar-menu desktop-menu">
            {navLinks.map(link => (
              <li key={link.href} className="navbar-item">
                <a href={link.href} className="navbar-link" onClick={(e) => closeMenuAndScroll(e, link.href)}>{link.text}</a>
              </li>
            ))}
          </ul>

          <div className="navbar-controls">
            <button
              onClick={toggleMusic}
              className={`navbar-music-toggle ${isMusicPlaying ? 'music-playing' : ''}`}
              aria-label={isMusicPlaying ? "Couper la musique" : "Activer la musique"}
              title={isMusicPlaying ? "Couper la musique" : "Activer la musique"}
            >
              {isMusicPlaying ? <FiVolume2 /> : <FiVolumeX />}
            </button>

            <div className="navbar-social-links-desktop">
              {/* Container pour le menu déroulant */}
              <div className="navbar-user-menu-container" ref={dropdownRef}>
                <button
                  className={`navbar-social-icon ${isUserLoggedIn ? 'logged-in' : ''}`}
                  aria-label={isUserLoggedIn ? "Menu Utilisateur" : "Se connecter"}
                  title={isUserLoggedIn ? `Menu (${user?.email})` : "Se connecter / S'inscrire"}
                  onClick={handleUserIconClick}
                >
                  {isUserLoggedIn ? (user?.role === 'admin' ? <FiSettings /> : <FiUser />) : <FiUser />}
                </button>

                {/* Menu Déroulant */}
                {dropdownOpen && isUserLoggedIn && (
                  <div className="user-dropdown-menu">
                    {/* Lien Admin (si admin) */}
                    {user?.role === 'admin' && (
                      <button className="dropdown-item" onClick={() => navigateTo('/admin')}>
                        <FiGrid /> Espace Admin
                      </button>
                    )}

                    {/* Lien Profil */}
                    <button className="dropdown-item" onClick={() => navigateTo('/profil')}>
                      <FiUser /> Mon Compte
                    </button>

                    {/* Lien Déconnexion */}
                    <button className="dropdown-item logout-item" onClick={handleLogout}>
                      <FiLogOut /> Se déconnecter
                    </button>
                  </div>
                )}
              </div>

              <a
                href={facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="navbar-social-icon"
                aria-label="Facebook"
              >
                <FiFacebook />
              </a>
              <a
                href={instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="navbar-social-icon"
                aria-label="Instagram"
              >
                <FiInstagram />
              </a>
            </div>

            <button
              className={`navbar-toggle ${isMobileMenuOpen ? 'open' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Menu"
            >
              <div className="burger-line line1"></div>
              <div className="burger-line line2"></div>
              <div className="burger-line line3"></div>
            </button>
          </div>

          <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
            <ul className="mobile-menu-items">
              <li className="mobile-navbar-item">
                <a href="#hero" className="mobile-navbar-link" onClick={(e) => closeMenuAndScroll(e, "#hero")}>Accueil</a>
              </li>
              {navLinks.map(link => (
                <li key={`mobile-${link.href}`} className="mobile-navbar-item">
                  <a href={link.href} className="mobile-navbar-link" onClick={(e) => closeMenuAndScroll(e, link.href)}>{link.text}</a>
                </li>
              ))}

              {/* Liens Mobile (pas de dropdown, juste la liste) */}
              <li className="mobile-menu-separator"></li>

              {!isUserLoggedIn && (
                <li className="mobile-navbar-item">
                  <a href="#" className="mobile-navbar-link" onClick={(e) => { closeMenuAndScroll(e); setAuthModalOpen(true); }}>
                    Connexion / Inscription
                  </a>
                </li>
              )}

              {isUserLoggedIn && user?.role === 'admin' && (
                <li className="mobile-navbar-item">
                  <a href="#" className="mobile-navbar-link" onClick={(e) => { closeMenuAndScroll(e); navigate('/admin'); }}>
                    Espace Admin
                  </a>
                </li>
              )}

              {isUserLoggedIn && (
                <>
                  <li className="mobile-navbar-item">
                    <a href="#" className="mobile-navbar-link" onClick={(e) => { closeMenuAndScroll(e); navigate('/profil'); }}>
                      Mon Compte
                    </a>
                  </li>
                  <li className="mobile-navbar-item">
                    <a href="#" className="mobile-navbar-link" style={{ color: '#e03131' }} onClick={(e) => { closeMenuAndScroll(e); handleLogout(e); }}>
                      Se déconnecter
                    </a>
                  </li>
                </>
              )}
            </ul>

            <div className="mobile-social-icons">
              {/* Icône Mobile */}
              <a href="#" className="mobile-social-icon-link" onClick={(e) => {
                closeMenuAndScroll(e);
                if (isUserLoggedIn) navigate('/profil');
                else setAuthModalOpen(true);
              }}>
                {isUserLoggedIn ? <FiUser /> : <FiUser />}
              </a>

              <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="mobile-social-icon-link">
                <FiFacebook />
              </a>
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="mobile-social-icon-link">
                <FiInstagram />
              </a>
            </div>
          </div>
        </div>
      </nav>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onLoginSuccess={() => {
          checkAuthStatus();
          setTimeout(() => {
            const u = getCurrentUser();
            if (u?.role === 'admin') navigate('/admin');
            else navigate('/profil');
          }, 500);
        }}
      />
    </>
  );
}

export default Navbar;